/**
 * Centralized API Logging Utility
 *
 * Provides standardized logging for all API calls including:
 * - Endpoint URL
 * - HTTP method
 * - Request payload
 * - Response status
 * - Error details
 *
 * This ensures consistent debugging information across all API calls.
 */

import clientLogger from './clientLogger';

/**
 * Enhanced fetch wrapper with automatic logging
 *
 * @param {string} url - The endpoint URL
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @param {string} context - Context for logging (e.g., 'payment_processing', 'user_registration')
 * @returns {Promise<Response>} - Fetch response
 */
export const loggedFetch = async (url, options = {}, context = 'api_call') => {
    const method = options.method || 'GET';
    const headers = options.headers || {};

    // Log the request details
    console.log(`[API] ${method} ${url}:`, {
        url,
        method,
        headers,
        body: options.body,
        context
    });

    try {
        const response = await fetch(url, options);

        // Log successful responses
        if (response.ok) {
            console.log(`[API] ${method} ${url} - Success:`, {
                status: response.status,
                statusText: response.statusText,
                context
            });
        } else {
            // Log error responses
            console.warn(`[API] ${method} ${url} - Error:`, {
                status: response.status,
                statusText: response.statusText,
                context
            });
        }

        return response;

    } catch (error) {
        // Log network/fetch errors
        console.error(`[API] ${method} ${url} - Network Error:`, {
            error: error.message,
            context
        });

        // Log to clientPrysm logger with full context
        clientLogger.logError('api_call', `API call failed: ${method} ${url}`, error, {
            action: context,
            endpoint: url,
            method: method,
            headers: headers,
            payload: options.body,
            errorType: 'network_error'
        });

        throw error;
    }
};

/**
 * Enhanced fetch wrapper that automatically logs API errors to the server
 *
 * @param {string} url - The endpoint URL
 * @param {Object} options - Fetch options
 * @param {string} context - Context for logging
 * @returns {Promise<Response>} - Fetch response
 */
export const loggedFetchWithErrorReporting = async (url, options = {}, context = 'api_call') => {
    const method = options.method || 'GET';

    try {
        const response = await loggedFetch(url, options, context);

        // If response is not ok, log detailed error information
        if (!response.ok) {
            const errorDetails = {
                endpoint: url,
                method: method,
                status: response.status,
                statusText: response.statusText,
                headers: options.headers || {},
                payload: options.body,
                context: context
            };

            console.error(`[API] ${method} ${url} - Detailed Error:`, errorDetails);

            // Create a structured error for logging
            const apiError = new Error(`API call failed: ${response.status} ${response.statusText}`);
            apiError.endpoint = url;
            apiError.method = method;
            apiError.status = response.status;
            apiError.statusText = response.statusText;

            // Log to clientPrysm logger with full context
            clientLogger.logError('api_error', `API call failed: ${method} ${url}`, apiError, {
                action: context,
                endpoint: url,
                method: method,
                status: response.status,
                statusText: response.statusText,
                headers: options.headers || {},
                payload: options.body,
                errorType: 'http_error'
            });
        }

        return response;

    } catch (error) {
        // Network errors are already logged in loggedFetch
        throw error;
    }
};

/**
 * Specialized payment API logging
 *
 * @param {string} url - Payment endpoint URL
 * @param {Object} options - Fetch options
 * @param {Object} paymentContext - Additional payment-specific context
 * @returns {Promise<Response>} - Fetch response
 */
export const loggedPaymentFetch = async (url, options = {}, paymentContext = {}) => {
    const enhancedContext = {
        ...paymentContext,
        action: 'payment_processing',
        paymentDetails: {
            planId: paymentContext.planId || 'unknown',
            amount: paymentContext.amount || 'unknown',
            promoCode: paymentContext.promoCode || null,
            cardToken: paymentContext.cardToken || 'missing',
            billingAddress: paymentContext.billingAddress || 'missing'
        }
    };

    return loggedFetchWithErrorReporting(url, options, 'payment_processing');
};

/**
 * Specialized user registration API logging
 *
 * @param {string} url - Registration endpoint URL
 * @param {Object} options - Fetch options
 * @param {Object} registrationContext - Additional registration-specific context
 * @returns {Promise<Response>} - Fetch response
 */
export const loggedRegistrationFetch = async (url, options = {}, registrationContext = {}) => {
    const enhancedContext = {
        ...registrationContext,
        action: 'user_registration',
        registrationDetails: {
            hasFirstName: !!registrationContext.firstName,
            hasEmail: !!registrationContext.email,
            hasPassword: !!registrationContext.password,
            fieldCount: registrationContext.fieldCount || 0
        }
    };

    return loggedFetchWithErrorReporting(url, options, 'user_registration');
};

/**
 * Specialized SMS verification API logging
 *
 * @param {string} url - SMS endpoint URL
 * @param {Object} options - Fetch options
 * @param {Object} smsContext - Additional SMS-specific context
 * @returns {Promise<Response>} - Fetch response
 */
export const loggedSmsFetch = async (url, options = {}, smsContext = {}) => {
    const enhancedContext = {
        ...smsContext,
        action: 'sms_verification',
        smsDetails: {
            phoneNumber: smsContext.phoneNumber || 'unknown',
            verificationCode: smsContext.verificationCode || 'missing',
            operation: smsContext.operation || 'unknown' // 'send' or 'verify'
        }
    };

    return loggedFetchWithErrorReporting(url, options, 'sms_verification');
};

export default {
    loggedFetch,
    loggedFetchWithErrorReporting,
    loggedPaymentFetch,
    loggedRegistrationFetch,
    loggedSmsFetch
};

